import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ulid } from "ulid";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { MultiSelectRoleCombobox } from "@/components/ui/multi-select-role-combobox";
import { uiStore } from "@/lib/stores/ui-store";
import { ws } from "@/lib/websocket-wrapper";

type PullTogetherDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	channelId: string;
};

export function PullTogetherDialog({
	open,
	onOpenChange,
	channelId,
}: PullTogetherDialogProps) {
	const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);

	const workspaceId = useStore(uiStore, (state) => state.currentWorkspaceId);

	const handleActivate = async () => {
		if (selectedRoles.length === 0) return;
		setLoading(true);
		try {
			const result = await ws.sendWithPromise<{ movedCount: number }>({
				id: ulid(),
				procedure: "livekit.pullTogether",
				data: {
					targetChannelId: channelId,
					roleKeys: selectedRoles,
				},
			});
			toast.success(`Pulling ${result.movedCount} user(s) together`);
			setSelectedRoles([]);
			onOpenChange(false);
		} catch (error) {
			console.error("Failed to pull together:", error);
			toast.error("Failed to pull together. You may not have permission.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog onOpenChange={onOpenChange} open={open}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Pull People</DialogTitle>
					<DialogDescription>
						Select roles to pull into this channel. Only users currently in a
						voice channel will be moved.
					</DialogDescription>
				</DialogHeader>
				<div className="py-2">
					<MultiSelectRoleCombobox
						hideEveryone
						onSelectionChange={setSelectedRoles}
						placeholder="Select roles..."
						selectedRoles={selectedRoles}
						workspaceId={workspaceId}
					/>
				</div>
				<DialogFooter>
					<Button
						disabled={selectedRoles.length === 0 || loading}
						onClick={handleActivate}
					>
						{loading && <Loader2 className="mr-2 size-4 animate-spin" />}
						Activate Pull
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
